import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CalendarDays, Clock, MapPin, Star, CheckCircle, Users, Loader2, Music, Trash2, ExternalLink, Target, Copy, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort } from '@/lib/datetime';
import { cn } from '@/lib/utils';

const FACES = [
    { rating: 1, label: 'Muy insatisfecho', emoji: '😠' },
    { rating: 2, label: 'Insatisfecho', emoji: '🙁' },
    { rating: 3, label: 'Neutral', emoji: '😐' },
    { rating: 4, label: 'Satisfecho', emoji: '🙂' },
    { rating: 5, label: 'Muy satisfecho', emoji: '😍' },
];

export default function CustomerClasses() {
    const [attendances, setAttendances] = useState([]);
    const [upcomingClasses, setUpcomingClasses] = useState([]);
    const [classesLoading, setClassesLoading] = useState(true);
    const [upcomingLoading, setUpcomingLoading] = useState(true);
    const [error, setError] = useState('');
    const [classMsg, setClassMsg] = useState({ type: '', text: '' });
    const [registeringId, setRegisteringId] = useState(null);
    const [unregisteringId, setUnregisteringId] = useState(null);

    const [ratingAttendanceId, setRatingAttendanceId] = useState(null);
    const [selectedRating, setSelectedRating] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [justSubmittedId, setJustSubmittedId] = useState(null);

    // Song suggestion state
    const [songPanelClassId, setSongPanelClassId] = useState(null);
    const [classSongs, setClassSongs] = useState({});
    const [songForm, setSongForm] = useState({ title: '', link: '' });
    const [songSubmitting, setSongSubmitting] = useState(false);
    const [songDeletingId, setSongDeletingId] = useState(null);
    const [copiedClassId, setCopiedClassId] = useState(null);

    // Class goals state
    const [goalsMap, setGoalsMap] = useState({});
    const [goalsLoadingId, setGoalsLoadingId] = useState(null);
    const [goalSubmittingId, setGoalSubmittingId] = useState(null);
    const [goalValues, setGoalValues] = useState({});

    useEffect(() => {
        const fetchAttendances = async () => {
            try {
                const res = await api.get('/customer/classes');
                setAttendances(res.data.data || []);
            } catch (err) {
                setError(err.response?.data?.message || 'Error al cargar las clases.');
            } finally { setClassesLoading(false); }
        };
        const fetchUpcoming = async () => {
            try {
                const res = await api.get('/customer/upcoming-classes');
                setUpcomingClasses(res.data.data || []);
            } catch {
                setUpcomingClasses([]);
            } finally { setUpcomingLoading(false); }
        };
        fetchAttendances();
        fetchUpcoming();
    }, []);

    const handleRegister = async (classId) => {
        setRegisteringId(classId);
        setClassMsg({ type: '', text: '' });
        try {
            const res = await api.post('/customer/register-to-class', { class_id: classId });
            setClassMsg({ type: 'success', text: res.data.message });
            setUpcomingClasses(prev => prev.map(cls =>
                cls.id === classId
                    ? { ...cls, inscripto: true, puede_desinscribirse: true, lugares_disponibles: cls.lugares_disponibles !== null ? Math.max(0, cls.lugares_disponibles - 1) : null }
                    : cls
            ));
        } catch (err) {
            setClassMsg({ type: 'error', text: err.response?.data?.message || 'Error al inscribirse.' });
        } finally {
            setRegisteringId(null);
        }
    };

    const handleUnregister = async (classId) => {
        setUnregisteringId(classId);
        setClassMsg({ type: '', text: '' });
        try {
            const res = await api.post('/customer/unregister-from-class', { class_id: classId });
            setClassMsg({ type: 'success', text: res.data.message });
            setUpcomingClasses(prev => prev.map(cls =>
                cls.id === classId
                    ? { ...cls, inscripto: false, puede_desinscribirse: false, lugares_disponibles: cls.lugares_disponibles !== null ? cls.lugares_disponibles + 1 : null }
                    : cls
            ));
            if (songPanelClassId === classId) {
                setSongPanelClassId(null);
                setClassSongs(prev => {
                    const next = { ...prev };
                    delete next[classId];
                    return next;
                });
            }
        } catch (err) {
            setClassMsg({ type: 'error', text: err.response?.data?.message || 'Error al desinscribirse.' });
        } finally {
            setUnregisteringId(null);
        }
    };

    const handleSubmitRating = async (attendance) => {
        if (!selectedRating) return;
        setSubmitting(true);
        try {
            await api.post('/customer/ratings', {
                class_attendance_id: attendance.id,
                rating: selectedRating,
                comment: comment.trim() || undefined,
            });
            setJustSubmittedId(attendance.id);
            setTimeout(() => {
                setAttendances(prev => prev.map(a => {
                    if (a.id !== attendance.id) return a;
                    return {
                        ...a,
                        rating: {
                            id: Date.now(),
                            rating: selectedRating,
                            comment: comment.trim() || null,
                        },
                    };
                }));
                setRatingAttendanceId(null);
                setSelectedRating(null);
                setComment('');
                setJustSubmittedId(null);
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al enviar la calificación.');
        } finally { setSubmitting(false); }
    };

    const openRating = (attendance) => {
        setRatingAttendanceId(attendance.id);
        setSelectedRating(null);
        setComment('');
        setError('');
    };

    const openSongPanel = async (classId) => {
        if (songPanelClassId === classId) {
            setSongPanelClassId(null);
            return;
        }
        setSongPanelClassId(classId);
        setSongForm({ title: '', link: '' });
        try {
            const res = await api.get(`/customer/classes/${classId}/songs`);
            setClassSongs(prev => ({
                ...prev,
                [classId]: res.data.data,
            }));
        } catch (err) {
            setClassSongs(prev => ({
                ...prev,
                [classId]: { songs: [], count: 0, limit: 5, is_registered: false },
            }));
        }
    };

    const handleSubmitSong = async (classId) => {
        if (!songForm.title.trim()) return;
        setSongSubmitting(true);
        try {
            const res = await api.post(`/customer/classes/${classId}/songs`, {
                title: songForm.title.trim(),
                link: songForm.link.trim() || undefined,
            });
            setClassSongs(prev => ({
                ...prev,
                [classId]: {
                    ...prev[classId],
                    songs: [...(prev[classId]?.songs || []), res.data.data],
                    count: (prev[classId]?.count || 0) + 1,
                },
            }));
            setSongForm({ title: '', link: '' });
        } catch (err) {
            setError(err.response?.data?.message || 'Error al sugerir la canción.');
        } finally {
            setSongSubmitting(false);
        }
    };

    const handleDeleteSong = async (classId, songId) => {
        setSongDeletingId(songId);
        try {
            await api.delete(`/customer/classes/${classId}/songs/${songId}`);
            setClassSongs(prev => ({
                ...prev,
                [classId]: {
                    ...prev[classId],
                    songs: (prev[classId]?.songs || []).filter(s => s.id !== songId),
                    count: Math.max(0, (prev[classId]?.count || 0) - 1),
                },
            }));
        } catch (err) {
            setError(err.response?.data?.message || 'Error al eliminar la canción.');
        } finally {
            setSongDeletingId(null);
        }
    };

    const formatCustomerSongsText = (classId, songsData) => {
        if (!songsData?.songs?.length) return '';
        const cls = upcomingClasses.find(c => c.id === classId);
        const header = `Canciones sugeridas — ${cls?.nombre || 'Clase'}`;
        const lines = songsData.songs.map((s, i) => {
            const linkLine = s.link ? `   Link: ${s.link}` : '';
            return `${i + 1}. ${s.title}${linkLine ? '\n' + linkLine : ''}`;
        });
        return [header, `${songsData.songs.length} cancione${songsData.songs.length !== 1 ? 's' : ''}`, '', ...lines].join('\n');
    };

    const handleCopyCustomerSongs = async (classId) => {
        const songsData = classSongs[classId];
        const text = formatCustomerSongsText(classId, songsData);
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setCopiedClassId(classId);
            setTimeout(() => setCopiedClassId(null), 2000);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopiedClassId(classId);
            setTimeout(() => setCopiedClassId(null), 2000);
        }
    };

    const handleDownloadCustomerSongs = (classId) => {
        const songsData = classSongs[classId];
        const text = formatCustomerSongsText(classId, songsData);
        if (!text) return;
        const cls = upcomingClasses.find(c => c.id === classId);
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `canciones-${cls?.nombre?.replace(/\s+/g, '-').toLowerCase() || 'clase'}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const toggleGoalsPanel = async (attendance) => {
        const classId = attendance.gym_class?.id;
        if (!classId) return;
        if (goalsMap[classId]) {
            setGoalsMap(prev => {
                const next = { ...prev };
                delete next[classId];
                return next;
            });
            return;
        }
        setGoalsLoadingId(classId);
        try {
            const res = await api.get(`/customer/classes/${classId}/goals`);
            setGoalsMap(prev => ({ ...prev, [classId]: res.data.data || [] }));
            const initialValues = {};
            (res.data.data || []).forEach(g => {
                if (!g.completion) {
                    initialValues[g.id] = '';
                }
            });
            setGoalValues(prev => ({ ...prev, ...initialValues }));
        } catch (err) {
            setError(err.response?.data?.message || 'Error al cargar objetivos.');
        } finally {
            setGoalsLoadingId(null);
        }
    };

    const handleSubmitGoal = async (classId, goalId) => {
        const value = goalValues[goalId];
        if (!value || isNaN(parseFloat(value))) return;
        setGoalSubmittingId(goalId);
        try {
            const res = await api.post(`/customer/classes/${classId}/goals/${goalId}/complete`, {
                value_achieved: parseFloat(value),
            });
            setGoalsMap(prev => ({
                ...prev,
                [classId]: (prev[classId] || []).map(g =>
                    g.id === goalId ? { ...g, completion: res.data.data } : g
                ),
            }));
            setGoalValues(prev => ({ ...prev, [goalId]: '' }));
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar el objetivo.');
        } finally {
            setGoalSubmittingId(null);
        }
    };

    if (classesLoading && upcomingLoading) return <p className="text-white/60 text-center py-8">Cargando...</p>;

    return (
        <div>
            <h1 className="text-2xl font-bold uppercase tracking-wide mb-6">Mis Clases</h1>

            {error && (
                <div className="mb-4 p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>
            )}

            {/* Past attendances */}
            {attendances.length === 0 ? (
                <Card className="bg-[#0a0a0a] border-hairline-dark mb-6">
                    <CardContent className="py-12 text-center">
                        <CalendarDays className="h-12 w-12 text-white/60 mx-auto mb-4" />
                        <p className="text-white/60">No tenés clases registradas.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 mb-6">
                    {attendances.map(a => {
                        const gymClass = a.gym_class;
                        const date = a.attended_at ? new Date(a.attended_at) : null;
                        const isRatingOpen = ratingAttendanceId === a.id;
                        const existingRating = a.rating;
                        const isJustSubmitted = justSubmittedId === a.id;
                        const face = existingRating ? FACES.find(f => f.rating === existingRating.rating) : null;

                        return (
                            <Card key={a.id} className="bg-[#0a0a0a] border-hairline-dark">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                            <CalendarDays className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-sm font-semibold uppercase tracking-wide">{gymClass?.name || 'Clase'}</CardTitle>
                                            <p className="text-xs text-white/60">{gymClass?.gym?.name || 'Gimnasio'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {existingRating ? (
                                            <Badge className="bg-white/10 text-white border-none text-xs flex items-center gap-1">
                                                <span className="text-sm leading-none">{face?.emoji}</span>
                                                <span>{face?.label}</span>
                                            </Badge>
                                        ) : a.attended_at ? (
                                            <button
                                                onClick={() => openRating(a)}
                                                className={cn(
                                                    "text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors",
                                                    isRatingOpen
                                                        ? "bg-white text-black"
                                                        : "bg-white/10 text-white hover:bg-white/20"
                                                )}
                                            >
                                                {isRatingOpen ? 'Calificando...' : 'Calificar'}
                                            </button>
                                        ) : null}
                                        <Badge variant={a.attended_at ? 'success' : 'secondary'} className="text-xs">
                                            {a.attended_at ? 'Asistió' : 'Inscripto'}
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <div className="flex flex-wrap items-center gap-4 text-xs text-white/60">
                                        {date && (
                                            <div className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                <span>{formatDateTimeShort(date)}</span>
                                            </div>
                                        )}
                                        {gymClass?.schedule && (
                                            <div className="flex items-center gap-1">
                                                <MapPin className="h-3 w-3" />
                                                <span>{Array.isArray(gymClass.schedule) ? gymClass.schedule.join(', ') : gymClass.schedule}</span>
                                            </div>
                                        )}
                                    </div>

                                    {isRatingOpen && !existingRating && (
                                        <div className="mt-4 pt-4 border-t border-hairline-dark">
                                            {isJustSubmitted ? (
                                                <div className="text-center py-4 space-y-2">
                                                    <CheckCircle className="h-8 w-8 text-green-400 mx-auto" />
                                                    <p className="text-sm font-bold text-white uppercase tracking-wide">
                                                        ¡Gracias por tu feedback!
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    <p className="text-xs font-medium text-white/80 text-center uppercase tracking-wider">
                                                        ¿Cómo fue tu experiencia?
                                                    </p>
                                                    <div className="flex justify-center gap-2 flex-wrap">
                                                        {FACES.map(face => (
                                                            <button
                                                                key={face.rating}
                                                                onClick={() => setSelectedRating(face.rating)}
                                                                className={cn(
                                                                    "flex flex-col items-center gap-1 p-2 rounded-lg transition-all min-w-[64px]",
                                                                    selectedRating === face.rating
                                                                        ? "bg-white/20 ring-1 ring-white scale-105"
                                                                        : "bg-white/5 hover:bg-white/10"
                                                                )}
                                                            >
                                                                <span className="text-2xl leading-none">{face.emoji}</span>
                                                                <span className="text-[10px] font-medium text-white/70 uppercase tracking-wider text-center leading-tight">
                                                                    {face.label}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>

                                                    <div>
                                                        <label className="text-[10px] font-medium text-white/60 uppercase tracking-wider mb-1.5 block">
                                                            Comentario o sugerencia (opcional)
                                                        </label>
                                                        <Textarea
                                                            value={comment}
                                                            onChange={e => setComment(e.target.value)}
                                                            placeholder="Contanos qué te gustó o qué podríamos mejorar..."
                                                            className="bg-black border-hairline-dark text-white placeholder:text-white/30 min-h-[80px] text-sm"
                                                            maxLength={1000}
                                                        />
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={() => handleSubmitRating(a)}
                                                            disabled={!selectedRating || submitting}
                                                            className="btn-ghost text-xs py-2 px-4"
                                                        >
                                                            {submitting ? 'Enviando...' : 'Enviar calificación'}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                setRatingAttendanceId(null);
                                                                setSelectedRating(null);
                                                                setComment('');
                                                            }}
                                                            className="text-white/60 hover:text-white text-xs"
                                                        >
                                                            Cancelar
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Class goals section for attended classes */}
                                    {a.attended_at && (
                                        <div className="mt-4 pt-4 border-t border-hairline-dark">
                                            <button
                                                onClick={() => toggleGoalsPanel(a)}
                                                className={cn(
                                                    "text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors flex items-center gap-1 mb-3",
                                                    goalsMap[gymClass?.id]
                                                        ? "bg-white text-black"
                                                        : "bg-white/10 text-white hover:bg-white/20"
                                                )}
                                            >
                                                <Target className="h-3 w-3" />
                                                {goalsMap[gymClass?.id] ? 'Ocultar objetivos' : 'Objetivos'}
                                            </button>

                                            {goalsLoadingId === gymClass?.id && (
                                                <p className="text-xs text-white/60">Cargando objetivos...</p>
                                            )}

                                            {goalsMap[gymClass?.id] && (
                                                <div className="space-y-3">
                                                    {goalsMap[gymClass.id].length === 0 && (
                                                        <p className="text-xs text-white/50">No hay objetivos para esta clase.</p>
                                                    )}
                                                    {goalsMap[gymClass.id].map(goal => {
                                                        const isCompleted = !!goal.completion;
                                                        const isSubmitting = goalSubmittingId === goal.id;
                                                        return (
                                                            <div key={goal.id} className="p-3 rounded-lg bg-white/5 border border-hairline-dark">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <p className="text-sm font-medium text-white">{goal.title}</p>
                                                                    <span className="text-xs text-white/60">
                                                                        Meta: {goal.target_value} {goal.unit || ''} — {goal.points} pts
                                                                    </span>
                                                                </div>
                                                                {isCompleted ? (
                                                                    <div className="flex items-center gap-2">
                                                                        <CheckCircle className={cn("h-4 w-4", goal.completion.reached ? "text-green-400" : "text-red-400")} />
                                                                        <span className="text-xs text-white/80">
                                                                            Hiciste {goal.completion.value_achieved} {goal.unit || ''}
                                                                            {goal.completion.reached
                                                                                ? ` — +${goal.completion.points_earned} pts`
                                                                                : ' — No alcanzado'}
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex gap-2 items-center">
                                                                        <Input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.01"
                                                                            value={goalValues[goal.id] || ''}
                                                                            onChange={e => setGoalValues(prev => ({ ...prev, [goal.id]: e.target.value }))}
                                                                            placeholder={`Cuánto hiciste?`}
                                                                            className="bg-black border-hairline-dark text-white placeholder:text-white/30 text-xs h-8 max-w-[140px]"
                                                                        />
                                                                        <Button
                                                                            size="sm"
                                                                            className="text-xs h-8"
                                                                            disabled={!goalValues[goal.id] || isSubmitting}
                                                                            onClick={() => handleSubmitGoal(gymClass.id, goal.id)}
                                                                        >
                                                                            {isSubmitting ? 'Guardando...' : 'Guardar'}
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Upcoming classes — moved to bottom */}
            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg text-white uppercase tracking-wide">
                        <CalendarDays className="h-5 w-5 text-white" />
                        Próximas Clases
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {classMsg.text && (
                        <div className={`text-sm p-3 rounded-md mb-4 border ${classMsg.type === 'success' ? 'border-green-800 bg-green-950/30 text-green-300' : 'border-red-800 bg-red-950/30 text-red-300'}`}>
                            {classMsg.text}
                        </div>
                    )}
                    {upcomingLoading ? (
                        <p className="text-white/60 text-center py-4">Cargando clases...</p>
                    ) : upcomingClasses.length === 0 ? (
                        <p className="text-white/60 text-center py-4">No hay clases próximas disponibles.</p>
                    ) : (
                        <div className="space-y-3">
                            {upcomingClasses.map(cls => {
                                const isSongPanelOpen = songPanelClassId === cls.id;
                                const songsData = classSongs[cls.id];

                                return (
                                    <div key={cls.id} className={`rounded-lg border border-hairline-dark hover:bg-white/5 ${cls.cancelada ? 'opacity-70' : ''}`}>
                                        <div className="flex items-center justify-between p-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-medium text-white truncate">{cls.nombre}</p>
                                                    {cls.cancelada && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/30">
                                                            Cancelada
                                                        </span>
                                                    )}
                                                    {cls.en_curso && !cls.cancelada && (
                                                        <span className="relative flex h-2.5 w-2.5 shrink-0" title="En curso">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                                                        </span>
                                                    )}
                                                    {cls.es_cercana && !cls.cancelada && (
                                                        <span className="relative flex h-2.5 w-2.5 shrink-0" title="Comienza en menos de 5 horas">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-500"></span>
                                                        </span>
                                                    )}
                                                </div>
                                                {cls.inicio && (
                                                    <p className="text-sm font-medium text-white mt-0.5">{cls.inicio}</p>
                                                )}
                                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/60 mt-1">
                                                    {cls.lugares_disponibles !== null && !cls.cancelada && (
                                                        <span className="flex items-center gap-1">
                                                            <Users className="h-3 w-3" />
                                                            {cls.lugares_disponibles} lugar{cls.lugares_disponibles !== 1 ? 'es' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                                {cls.cancelada && cls.mensaje_cancelacion && (
                                                    <div className="flex items-start gap-1.5 mt-1.5 text-xs text-red-300">
                                                        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                                        <span>{cls.mensaje_cancelacion}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="shrink-0 ml-3 flex items-center gap-2">
                                                {cls.cancelada ? (
                                                    <Badge variant="outline" className="text-red-400 border-red-500/30">
                                                        Cancelada
                                                    </Badge>
                                                ) : cls.expirada ? (
                                                    <Badge variant="outline" className="text-white/40 border-white/20">
                                                        Finalizada
                                                    </Badge>
                                                ) : cls.inscripto ? (
                                                    <>
                                                        <Badge variant="outline" className="flex items-center gap-1">
                                                            <CheckCircle className="h-3 w-3" /> Inscripto
                                                        </Badge>
                                                        <button
                                                            onClick={() => openSongPanel(cls.id)}
                                                            className={cn(
                                                                "text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors flex items-center gap-1",
                                                                isSongPanelOpen
                                                                    ? "bg-white text-black"
                                                                    : "bg-white/10 text-white hover:bg-white/20"
                                                            )}
                                                        >
                                                            <Music className="h-3 w-3" />
                                                            {isSongPanelOpen ? 'Canciones' : 'Sugerir'}
                                                        </button>
                                                        {cls.puede_desinscribirse && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-white border-white/30 hover:bg-white/10"
                                                                disabled={unregisteringId === cls.id}
                                                                onClick={() => handleUnregister(cls.id)}
                                                            >
                                                                {unregisteringId === cls.id ? 'Saliendo...' : 'Salir'}
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        disabled={registeringId === cls.id || (cls.lugares_disponibles !== null && cls.lugares_disponibles <= 0)}
                                                        onClick={() => handleRegister(cls.id)}
                                                    >
                                                        {registeringId === cls.id ? 'Inscribiendo...' : 'Inscribirme'}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {isSongPanelOpen && cls.inscripto && (
                                            <div className="border-t border-hairline-dark px-3 pb-3 pt-2">
                                                <div className="space-y-3">
                                                    <p className="text-xs font-medium text-white/80 uppercase tracking-wider flex items-center gap-1">
                                                        <Music className="h-3 w-3" />
                                                        Canciones sugeridas
                                                        {songsData && (
                                                            <span className="text-white/50 normal-case">
                                                                ({songsData.count}/{songsData.limit})
                                                            </span>
                                                        )}
                                                    </p>

                                                    {songsData?.songs?.length > 0 && (
                                                        <>
                                                            <div className="space-y-2">
                                                                {songsData.songs.map(song => (
                                                                    <div key={song.id} className="flex items-center justify-between p-2 rounded bg-white/5 border border-hairline-dark">
                                                                        <div className="min-w-0">
                                                                            <p className="text-sm text-white truncate">{song.title}</p>
                                                                            {song.link && (
                                                                                <a
                                                                                    href={song.link}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 truncate"
                                                                                >
                                                                                    <ExternalLink className="h-3 w-3" />
                                                                                    {song.link.replace(/^https?:\/\//, '').slice(0, 40)}...
                                                                                </a>
                                                                            )}
                                                                        </div>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-7 w-7 text-white/40 hover:text-red-400 shrink-0"
                                                                            disabled={songDeletingId === song.id}
                                                                            onClick={() => handleDeleteSong(cls.id, song.id)}
                                                                        >
                                                                            <Trash2 className="h-3.5 w-3.5" />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Button variant="outline" size="sm" onClick={() => handleCopyCustomerSongs(cls.id)} className="border-hairline-dark text-white hover:bg-white/5">
                                                                    {copiedClassId === cls.id ? <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-green-400" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                                                                    {copiedClassId === cls.id ? 'Copiado' : 'Copiar'}
                                                                </Button>
                                                                <Button variant="outline" size="sm" onClick={() => handleDownloadCustomerSongs(cls.id)} className="border-hairline-dark text-white hover:bg-white/5">
                                                                    <Download className="h-3.5 w-3.5 mr-1" />
                                                                    Descargar
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}

                                                    {(!songsData || songsData.is_registered) && (songsData?.count || 0) < 5 && (
                                                        <div className="space-y-2">
                                                            <div>
                                                                <label className="text-[10px] font-medium text-white/60 uppercase tracking-wider mb-1 block">
                                                                    Título / Artista
                                                                </label>
                                                                <Input
                                                                    value={songForm.title}
                                                                    onChange={e => setSongForm(prev => ({ ...prev, title: e.target.value }))}
                                                                    placeholder="Ej: Bad Bunny - Monaco"
                                                                    className="bg-black border-hairline-dark text-white placeholder:text-white/30 text-sm h-9"
                                                                    maxLength={255}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-medium text-white/60 uppercase tracking-wider mb-1 block">
                                                                    Link de YouTube o Spotify (opcional)
                                                                </label>
                                                                <Input
                                                                    value={songForm.link}
                                                                    onChange={e => setSongForm(prev => ({ ...prev, link: e.target.value }))}
                                                                    placeholder="https://..."
                                                                    className="bg-black border-hairline-dark text-white placeholder:text-white/30 text-sm h-9"
                                                                    maxLength={500}
                                                                />
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <Button
                                                                    onClick={() => handleSubmitSong(cls.id)}
                                                                    disabled={!songForm.title.trim() || songSubmitting}
                                                                    className="text-xs py-2 px-4"
                                                                    size="sm"
                                                                >
                                                                    {songSubmitting ? 'Enviando...' : 'Agregar canción'}
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setSongPanelClassId(null);
                                                                        setSongForm({ title: '', link: '' });
                                                                    }}
                                                                    className="text-white/60 hover:text-white text-xs"
                                                                >
                                                                    Cerrar
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {songsData && songsData.count >= 5 && (
                                                        <p className="text-xs text-white/50">
                                                            Ya alcanzaste el límite de 5 canciones para esta clase.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
